import React from 'react';

import { Link } from 'react-router-dom';

import { DashboardOutlined, EnvironmentOutlined, UserOutlined, ToolOutlined } from '@ant-design/icons';
import { ErpOrderStatus, TenantProductName } from '@recurrency/core-api-schema/dist/common/enums';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { ColumnType } from 'antd/lib/table';

import { ButtonLink, SalesCustomerLink } from 'components/Links';
import { ErpOrderStatusBadge } from 'components/recipes/ErpOrderStatusBadge';
import { NewOrderButton } from 'components/recipes/NewOrderButton';
import { SearchFrame } from 'components/recipes/SearchFrame';
import { ValueFacet } from 'components/recipes/SearchFrame/types';
import { Tooltip } from 'components/Tooltip';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { truthy } from 'utils/boolean';
import { filterCostAndGM } from 'utils/filterCostAndGM';
import { formatDate, toTitleCase } from 'utils/formatting';
import { isAdmin, shouldShowProduct, shouldUseCoreApiForSearch } from 'utils/roleAndTenant';
import { routes } from 'utils/routes';
import { sortableDateColumn, sortableDollarColumn, sortableNumberColumn, typedColumn } from 'utils/tables';

import { SearchIndexSalesOrder } from 'types/search-collections';

export const OrderListPage = () => {
  const { activeTenant, activeErpRole } = useGlobalApp();
  const isUserAdminRole = isAdmin(activeErpRole.foreignId, activeErpRole.name);
  const hasSalesQuotingProduct = shouldShowProduct(activeTenant, TenantProductName.SalesQuoting);
  const useCoreApiForSearch = shouldUseCoreApiForSearch(activeTenant);

  const columns: (ColumnType<SearchIndexSalesOrder> | null)[] = [
    typedColumn({
      title: 'Order No',
      dataIndex: 'order_no',
      render: (_, order: SearchIndexSalesOrder) => (
        <>
          <Link to={routes.orders.orderDetails(order.order_no)}>{order.order_no}</Link>
          {order.order_type === 'service-order' && (
            <>
              {' '}
              <Tooltip title="This order is a service order.">
                <ToolOutlined />
              </Tooltip>
            </>
          )}
        </>
      ),
    }),
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: string) => <ErpOrderStatusBadge erpOrderStatus={status.toLowerCase() as ErpOrderStatus} />,
    },
    sortableDateColumn({
      title: 'Order Date',
      dataIndex: 'order_date',
      defaultSortOrder: 'descend',
      sorter: true,
      render: (dt: string) => formatDate(dt),
    }),
    {
      title: 'Customer ID',
      dataIndex: 'customer_id',
      render: (customerId: string) => <SalesCustomerLink customerId={customerId}>{customerId}</SalesCustomerLink>,
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
    },
    sortableDollarColumn({
      title: 'Total Open Amount',
      dataIndex: 'total_open_value',
      sorter: true,
    }),
    sortableDollarColumn({
      title: 'Total Amount',
      dataIndex: 'total_value',
      sorter: true,
    }),
    sortableDollarColumn({
      title: 'GM',
      dataIndex: 'gross_margin',
      sorter: true,
    }),
    sortableNumberColumn({ title: 'Line Count', dataIndex: 'item_count', sorter: true }),
    {
      title: '',
      dataIndex: 'order_no',
      render: (orderNo: string) => ButtonLink(routes.orders.orderDetails(orderNo)),
    },
  ].filter(filterCostAndGM);

  const valueFacets: (ValueFacet<SearchIndexSalesOrder> | null)[] = [
    {
      title: 'Status',
      field: 'status',
      icon: <DashboardOutlined />,
      labelFormatFn: toTitleCase,
    },
    // In typesense, orders and quotes are unioned in the same collection, so they need to be filtered by order_type.
    // core-api which queries recurrency_sales_orders only doesn't need order_type filter as it only returns orders.
    useCoreApiForSearch
      ? null
      : {
          field: 'order_type',
          hidden: true,
          defaultValue: 'order',
        },
    {
      title: 'Order Taker',
      field: 'taker',
      queryPlaceholder: 'Search takers',
      icon: <UserOutlined />,
    },
    hasSalesQuotingProduct && isUserAdminRole && !useCoreApiForSearch
      ? {
          title: 'Sales Rep',
          queryPlaceholder: 'Search sales reps',
          field: 'salesrep_name',
          icon: <UserOutlined />,
        }
      : null,
    {
      title: 'Location',
      field: 'location_name',
      queryPlaceholder: 'Search locations',
      icon: <EnvironmentOutlined />,
    },
  ];

  return (
    <SearchFrame<SearchIndexSalesOrder>
      title={
        <InfoTooltip title="Recurrency imports 1 year of orders for search, but includes all of them when calculating statistics.">
          Orders
        </InfoTooltip>
      }
      indexName={SearchIndexName.SalesOrders}
      queryPlaceholder="Search recent orders by order no, customer, ship to, PO, salesrep, or location"
      columns={columns.filter(truthy)}
      valueFacets={valueFacets.filter(truthy)}
      headerActions={hasSalesQuotingProduct ? <NewOrderButton /> : null}
    />
  );
};
